import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NottinghamshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Nottinghamshire Pothole Claims | 416,380 Potholes in Five Years | Fixtyer"
        description="Nottinghamshire County Council filled 416,380 potholes in five years — around 228 every day — while 62% of its network is surveyed on a three-year cycle. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/nottinghamshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Nottinghamshire', url: '/councils/nottinghamshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Nottinghamshire filled over 416,000 potholes in five years — does that help my claim?',
            answer: 'Yes. The council\'s own transparency report records 416,380 potholes filled between 2020/21 and 2024/25 — an average of around 228 every day, peaking at 100,834 in a single year. A network producing defects at that rate is one where potholes routinely form between inspections, which is exactly the scenario where prior reports and dated photographs decide claims.',
          },
          {
            question: 'What if my pothole was on an unclassified or residential road?',
            answer: 'Unclassified roads make up 2,744km — 62% of Nottinghamshire\'s network — and have traditionally been condition-surveyed in thirds, so the council says "it takes 3 years to build a complete view" of their condition. RED-rated unclassified roads rose from 25% in 2020 to 35% in 2023. The council\'s own report concedes that condition data "may be up to three years old" and that delayed interventions risk "compromising safety".',
          },
          {
            question: 'Why did RED-rated B and C roads jump from 4% to 12% in 2024?',
            answer: 'Because the measuring tool changed. The 2024 figures were "calculated using non-SCANNER technology" — a new AI video capture system — and Nottinghamshire has commissioned a 2025/26 SCANNER survey to compare the two methods and, in its own words, "if necessary - normalise our 2024/25 results retrospectively". Either the new system is revealing deterioration the old one missed, or the council\'s condition records are not comparable across years. Both readings give a Section 58 defence questions to answer.',
          },
          {
            question: 'Nottinghamshire\'s DfT Spend rating is GREEN — can I still claim?',
            answer: 'Yes. Spend is only one scorecard: the overall rating is AMBER because Condition and Best Practice are both AMBER. For four consecutive years — 2020/21 to 2023/24 — the council\'s own figures show a higher estimated share of spend on reactive maintenance than preventative maintenance, peaking at 39% reactive against 18% preventative in 2022/23. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate budgets.',
          },
          {
            question: 'Does the fall in RED unclassified roads from 35% to 20% weaken my claim?',
            answer: 'No — it is not a comparable figure. The council\'s footnote states the 2024 number was "calculated using new video capture system", while the 25-35% figures came from Coarse Visual Inspection surveys covering a third of the network each year. A fifteen-point fall in a single year coincides exactly with a change of measuring tool, and the council itself is still working out how the two methodologies relate to each other.',
          },
          {
            question: 'Is Nottinghamshire rated as badly as neighbouring Derbyshire?',
            answer: 'No. Derbyshire is one of the few councils rated RED overall by the DfT; Nottinghamshire is AMBER with a GREEN spend scorecard. Expect a more organised Section 58 defence from Nottinghamshire — which means your claim needs to be specific: prior reports of the same defect, dated photographs, the road\'s class, and the survey-cycle gap for unclassified roads.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Nottinghamshire County Council" 
        councilSlug="nottinghamshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/nottinghamshire"
        headline="Nottinghamshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Nottinghamshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Nottinghamshire: 416,380 Potholes in Five Years
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Nottinghamshire County Council earns a GREEN spend scorecard and filled more than 
            <strong className="text-rose-400"> 416,000 potholes between 2020/21 and 2024/25</strong> — 
            around 228 every day. Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong>: 
            62% of the network is surveyed on a three-year cycle, RED-rated unclassified roads climbed 
            from 25% to 35%, and the council's own report admits stale condition data risks 
            "compromising safety".
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">100,834</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2021/22 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              That is roughly 276 potholes every single day for a year — on a 4,428km network 
              with an £8.1 billion replacement cost. The five-year total is 416,380.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Pothole Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Pothole Conveyor Belt
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of the council's own "estimate of the number of potholes filled" — 
            one of the largest repair volumes published by any English authority
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Average per day</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">95,752</td>
                  <td className="px-6 py-4 text-center">~262</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">100,834</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~276</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">80,752</td>
                  <td className="px-6 py-4 text-center">~221</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">62,288</td>
                  <td className="px-6 py-4 text-center">~171</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">76,754</td>
                  <td className="px-6 py-4 text-center">~210</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">416,380</td>
                  <td className="px-6 py-4 text-center font-bold">~228</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What 416,380 Repairs Tells You</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council defines the figure as "the number of actionable defects repaired" — 
                defects that crossed its own intervention thresholds. Averaged over five years, 
                that is roughly 94 actionable defects per kilometre of road.
              </p>
              <p className="text-surface-600 text-sm">
                A network producing defects at this rate is, by definition, one where potholes 
                routinely form between inspections — exactly the scenario where prior reports 
                and dated photographs decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Temporary Repair Admission</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council aims for "right first time" repairs, but concedes that "where network 
                deterioration is significant in combination with severe weather... a temporary repair 
                will be undertaken", to be "followed up by a permanent repair".
              </p>
              <p className="text-amber-800 text-sm">
                If the pothole that damaged your vehicle was a failed temporary patch, the repair 
                history of that exact defect becomes central to your claim.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The 2024/25 Rebound</h3>
            <p className="text-brand-200 mb-4">
              Pothole numbers fell from 100,834 (2021/22) to 62,288 (2023/24) — then climbed back 
              to <strong className="text-amber-400">76,754 in 2024/25</strong>, a 23% year-on-year rise. 
              The council forecasts another 60,300 fills in 2025/26, while acknowledging "it is 
              difficult to accurately determine the number of potholes to be repaired".
            </p>
            <p className="text-white font-medium">
              Even on the council's most optimistic forecast, Nottinghamshire expects to fill about 
              165 potholes a day this year. This is not a network that has been fixed — it is a 
              network being continuously firefought.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Nottinghamshire's own transparency report — 
            every road class trending the wrong way until the measuring tool changed
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (555km — 12.5% of network): slowly slipping</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">4%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">doubled from 2% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">16%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 13% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">80%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 85% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads remain the best-kept part of the network, but the direction of travel is 
              steady decline: RED has doubled and GREEN has fallen five points since 2020.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,128km — 25.5% of network): RED tripled in 2024</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">63%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              *2024 figures were "calculated using non-SCANNER technology" — the new AI video survey. 
              When the camera replaced the laser, RED jumped from 4% to 12% and GREEN dropped eight 
              points. More than a third of the B/C network now needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And The Spend Scorecard Is GREEN</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£21.0m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£38.2m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£37m</div>
                <div className="text-brand-300 text-sm">Extra council investment over five years</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Nottinghamshire spent <span className="text-emerald-400">82% more than its DfT allocation</span> in 
              2024/25 and still filled 76,754 potholes that year. The chequebook is open — 
              the network is deteriorating faster than the money can catch it.
            </p>
          </div>
        </div>
      </section>

      {/* The Three-Year Blind Spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,744km Three-Year Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            62% of Nottinghamshire's network is unclassified — and surveyed one third at a time
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Unclassified roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-medium">20%* <span className="text-surface-400 text-xs">(new video capture system — not comparable)</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">A Third of the Network Per Year</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council's traditional Coarse Visual Inspection "covers around a third of our 
                unclassified network per year meaning that it takes 3 years to build a complete view 
                of our unclassified carriageways' condition."
              </p>
              <p className="text-surface-600 text-sm">
                On the last fully comparable survey basis, <strong>35% of unclassified roads were in RED 
                condition</strong> — roughly 960km of residential streets, estate roads and village 
                routes flagged as needing maintenance consideration. That RED share rose every 
                single year from 2020 to 2023: 25%, 29%, 31%, 35%.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 20% Asterisk</h3>
              <p className="text-amber-800 text-sm mb-3">
                The 2024 figure of 20% was "calculated using new video capture system" — the AI-based 
                Vaisala Road AI tool — not the Coarse Visual Inspection used for every previous year 
                in the table.
              </p>
              <p className="text-amber-800 text-sm">
                A fifteen-point improvement in a single year, coinciding exactly with a change of 
                measuring tool, is not a recovery — it is a different ruler. The council is still 
                running a parallel SCANNER survey in 2025/26 to work out how its old and new 
                numbers relate.
              </p>
            </div>
          </div>

          <h3 className="font-semibold text-brand-900 mb-4">The Council's Own Words On Stale Data</h3>
          <p className="text-surface-600 text-sm mb-6">
            Nottinghamshire's transparency report includes an unusually candid list of the 
            consequences of surveying unclassified roads on a three-year cycle. These are verbatim:
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Condition data may be up to three years old, this can limit our responsiveness to emerging issues and reduce the accuracy of whole-network condition assessments"
              source="Nottinghamshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="Delaying interventions may lead to minor defects developing into major defects increasing maintenance costs and compromising safety"
              source="Nottinghamshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="There is also the likelihood that some areas might deteriorate faster than anticipated between survey cycles, especially in high-traffic or weather-sensitive areas"
              source="Nottinghamshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing the condition of its roads. For Nottinghamshire's unclassified network — 62% 
              of the county — ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — this year, last year, or three years ago?</li>
              <li>• If 35% of unclassified roads were RED at the last comparable survey, what was done about yours?</li>
              <li>• The council admits stale data risks "compromising safety" — how does that square with a defence of reasonable care?</li>
              <li>• Was your road in a "high-traffic or weather-sensitive" area the council itself flags as deteriorating faster than anticipated?</li>
            </ul>
            <p className="text-white font-medium">
              It is rare for a highway authority to put the weaknesses of its own survey regime in 
              writing. Nottinghamshire has — and those admissions are fair material for any claim 
              on an unclassified road.
            </p>
          </div>
        </div>
      </section>

      {/* The Spending Story */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Four Years of Firefighting
          </h2>
          <p className="text-surface-600 mb-8">
            From 2020/21 to 2023/24 the council's own estimates show reactive maintenance taking 
            a bigger share of spend than preventative maintenance — the pattern is only now reversing
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-4 py-4 text-center">£24.6m</td>
                  <td className="px-4 py-4 text-center">£24.5m</td>
                  <td className="px-4 py-4 text-center">17%</td>
                  <td className="px-4 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-4 py-4 text-center">£18.6m</td>
                  <td className="px-4 py-4 text-center">£20.6m</td>
                  <td className="px-4 py-4 text-center">18%</td>
                  <td className="px-4 py-4 text-center font-bold text-rose-600">29%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-4 py-4 text-center">£18.6m</td>
                  <td className="px-4 py-4 text-center">£22.3m</td>
                  <td className="px-4 py-4 text-center">18%</td>
                  <td className="px-4 py-4 text-center font-bold text-rose-600">39%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-4 py-4 text-center">£24.3m</td>
                  <td className="px-4 py-4 text-center">£31.1m</td>
                  <td className="px-4 py-4 text-center">22%</td>
                  <td className="px-4 py-4 text-center font-bold text-rose-600">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-4 py-4 text-center">£21.0m</td>
                  <td className="px-4 py-4 text-center">£38.2m</td>
                  <td className="px-4 py-4 text-center font-bold text-emerald-600">32%</td>
                  <td className="px-4 py-4 text-center">14%</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 font-mono text-brand-900">2025/26 <span className="text-surface-400 text-xs">(projected)</span></td>
                  <td className="px-4 py-4 text-center">£44.7m</td>
                  <td className="px-4 py-4 text-center">£52.4m</td>
                  <td className="px-4 py-4 text-center font-bold text-emerald-600">36%</td>
                  <td className="px-4 py-4 text-center">15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The Pattern Shows</h3>
              <p className="text-surface-600 text-sm mb-3">
                In 2022/23, an estimated 39% of spend went on reactive maintenance against just 18% 
                preventative — more than two pounds spent patching for every pound spent preventing. 
                The council says around 60% of all reactive repair funding goes on pothole repairs.
              </p>
              <p className="text-surface-600 text-sm">
                Years of firefighting are precisely what the condition tables record: unclassified 
                RED rising every year and 416,380 potholes filled. The rebalancing towards prevention 
                only began in 2024/25.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why GREEN Spend Doesn't Block Your Claim</h3>
              <p className="text-amber-800 text-sm mb-3">
                The DfT's GREEN spend scorecard reflects money committed — and Nottinghamshire 
                genuinely commits it, spending £38.2m against a £21.0m allocation in 2024/25 plus 
                an extra £37m of its own funds over five years.
              </p>
              <p className="text-amber-800 text-sm">
                But Section 58 is not about budgets. It asks whether the specific stretch of road 
                that damaged your vehicle was inspected and maintained reasonably. Aggregate spend 
                doesn't repair the pothole you hit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Methodology Switch */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Measuring-Tool Problem
          </h2>
          <p className="text-surface-600 mb-8">
            In 2024/25 Nottinghamshire swapped its laser survey for AI video — and the numbers 
            moved sharply in both directions
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The results of the 2025/26 SCANNER survey will be compared with those from our video capture system. This will allow us to better understand the relationship between the outputs obtained from the two different surveying methodologies prior to the introduction of PAS2161 and - if necessary - normalise our 2024/25 results retrospectively."
              source="Nottinghamshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What Changed</h3>
              <p className="text-surface-600 text-sm mb-3">
                Until 2023/24, classified roads were surveyed by SCANNER laser — one direction per 
                year, so full coverage took two years. Unclassified roads got a visual inspection 
                of one third of the network annually. From 2024/25, an AI video capture system 
                (Vaisala Road AI) surveys 100% of the network every year.
              </p>
              <p className="text-surface-600 text-sm">
                Annual full-network coverage is a genuine improvement. But it means every 2024 
                condition figure on this page carries a footnote: "calculated using non-SCANNER 
                technology".
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Contradiction</h3>
              <p className="text-amber-800 text-sm mb-3">
                Under the new system, B/C roads got dramatically <strong>worse</strong> (RED tripled, 
                4% to 12%) while unclassified roads got dramatically <strong>better</strong> (RED 
                fell 35% to 20%) — in the same year, measured by the same camera.
              </p>
              <p className="text-amber-800 text-sm">
                Either the old surveys understated B/C decay for years, or the new system is not 
                yet calibrated. The council admits it may have to "normalise" its own 2024/25 
                results once the comparison survey reports. Until then, its condition records 
                straddle two incompatible rulers.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">How To Use This In A Claim</h3>
            <p className="text-brand-200 mb-4">
              A Section 58 defence leans on condition records. In Nottinghamshire, those records 
              are in transition:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Pre-2024 SCANNER data and post-2024 AI data cannot be directly compared — the council says so itself</li>
              <li>• If the council cites the improved 20% unclassified figure, the footnote says it came from a different methodology than every previous year</li>
              <li>• If the 12% B/C figure is accurate, more than one in ten B and C road kilometres should currently "be considered for maintenance"</li>
              <li>• Results may be retrospectively "normalised" — meaning today's published figures are provisional</li>
            </ul>
            <p className="text-white font-medium">
              A defence built on condition data the council itself may restate is a defence worth 
              probing hard.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Spend-Heavy AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Nottinghamshire is not neighbouring Derbyshire, the East Midlands' 
            notorious RED council — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £38.2m spent against a £21.0m allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £37m of its own money added to highways over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented inspection regime — the Highway Inspection and Risk Manual (HIRM) sets defect investigation levels
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Moving to annual full-network AI condition surveys, with 88.2km of carriageway resurfaced in 2024/25 — double the previous year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LGA peer review commissioned and a Highways Improvement Plan delivered
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect an organised Section 58 defence with inspection logs. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 416,380 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition and AMBER best practice — only spend is GREEN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 62% of the network surveyed on a three-year cycle, with RED rising every year to 35%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Written admission that stale condition data risks "compromising safety"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive spend share exceeded preventative for four straight years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Condition records split across two incompatible survey methodologies, pending retrospective "normalisation"
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that spends heavily and documents its inspections, your claim lives 
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, MyNotts app, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age — weathered edges, previous patching, failed temporary repairs</li>
              <li>• The road's class — on an unclassified road, the three-year survey cycle and the council's own stale-data admissions are your strongest structural arguments</li>
              <li>• Whether your road sits in a "high-traffic or weather-sensitive" area the council itself flags as deteriorating between surveys</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo 
              evidence, and cites Nottinghamshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Nottinghamshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A council that fills 416,000 potholes keeps records — your claim needs to match them. 
            £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No three-year survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No methodology-change analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 416,380 potholes in five years cited</li>
                <li>✅ Three-year unclassified survey gap argued</li>
                <li>✅ Council's own "compromising safety" admission quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Nottinghamshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=nottinghamshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Nottinghamshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Nottinghamshire filled over 416,000 potholes in five years — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The council's own transparency report records 416,380 potholes filled between 
                2020/21 and 2024/25 — an average of around 228 every day, peaking at 100,834 in a 
                single year. A network producing defects at that rate is one where potholes routinely 
                form between inspections, which is exactly the scenario where prior reports and dated 
                photographs decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 2,744km — 62% of Nottinghamshire's network — and have 
                traditionally been condition-surveyed in thirds, so the council says "it takes 3 years 
                to build a complete view" of their condition. RED-rated unclassified roads rose from 
                25% in 2020 to 35% in 2023. The council's own report concedes that condition data 
                "may be up to three years old" and that delayed interventions risk "compromising safety".
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did RED-rated B and C roads jump from 4% to 12% in 2024?
              </h3>
              <p className="text-surface-600 text-sm">
                Because the measuring tool changed. The 2024 figures were "calculated using non-SCANNER 
                technology" — a new AI video capture system — and Nottinghamshire has commissioned a 
                2025/26 SCANNER survey to compare the two methods and, in its own words, "if necessary - 
                normalise our 2024/25 results retrospectively". Either the new system is revealing 
                deterioration the old one missed, or the council's condition records are not comparable 
                across years. Both readings give a Section 58 defence questions to answer.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Nottinghamshire's DfT Spend rating is GREEN — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Spend is only one scorecard: the overall rating is AMBER because Condition and 
                Best Practice are both AMBER. For four consecutive years — 2020/21 to 2023/24 — the 
                council's own figures show a higher estimated share of spend on reactive maintenance 
                than preventative maintenance, peaking at 39% reactive against 18% preventative in 
                2022/23. Section 58 turns on whether the specific defect that damaged your vehicle 
                was reasonably inspected and repaired, not on aggregate budgets.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the fall in RED unclassified roads from 35% to 20% weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it is not a comparable figure. The council's footnote states the 2024 number was 
                "calculated using new video capture system", while the 25-35% figures came from Coarse 
                Visual Inspection surveys covering a third of the network each year. A fifteen-point 
                fall in a single year coincides exactly with a change of measuring tool, and the 
                council itself is still working out how the two methodologies relate to each other.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is Nottinghamshire rated as badly as neighbouring Derbyshire?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Derbyshire is one of the few councils rated RED overall by the DfT; Nottinghamshire 
                is AMBER with a GREEN spend scorecard. Expect a more organised Section 58 defence from 
                Nottinghamshire — which means your claim needs to be specific: prior reports of the 
                same defect, dated photographs, the road's class, and the survey-cycle gap for 
                unclassified roads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.nottinghamshire.gov.uk/transport/roads/dft-funding"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Nottinghamshire County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
